import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { MdDelete } from "react-icons/md";

import DropZone, { formatFileSize } from "../FileUpload/DropZone";
import Button from "../../Button";
import Spinner from "../../Loaders/Spinner";
import SplashLoader from "../../Loaders/SplashLoader";
import ErrorMessages from "../../Notifications/ErrorMessages";
import { FormControl, StyledInput, Label } from "../../Form/FormControls";
import { useApi } from "../../../api/useApi";
import PermissionsSelect from "../../Form/PermissionsSelect";
import useUploadFile from "../../../Hooks/useUploadFile";
import useUploadedSourceCollision from "../../../Hooks/useUploadedSourceCollision";
import { importDataSources } from "../../../api/connectionMutations";
import { replaceUploadedFile } from "../../../api/dataSourceMutations";
import useConnectionImportFinished from "../../../Hooks/useConnectionImportFinished";

/**
 * Turns a file on the user's machine into a data source.
 *
 * Replaces the remote file browser for Local File Upload connections: there is
 * nothing to browse, so the file is uploaded first and then imported through the
 * same mutation the browser uses, producing an ordinary file-backed source.
 */

// Kept in step with the server, which enforces the same limit independently.
const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".csv", ".xlsx"];

const StagedFile = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;

  button {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
  }
`;

const Progress = styled.div`
  margin-top: 0.75rem;
  font-size: 0.9rem;
  opacity: 0.7;
`;

// Creating the source hands off to a background runner, so the wait is longer than a
// request and there is nothing on screen to show for it. Covering the form says the
// work is in progress and stops the button being pressed twice meanwhile.
const Creating = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  background: rgba(255, 255, 255, 0.7);
`;

const Actions = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
`;

// Stands out from the ordinary form copy without reading as a failure: nothing has gone
// wrong, there is simply a choice to make.
const Collision = styled.div`
  border: 1px solid #d9a406;
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 1rem;

  p {
    margin: 0 0 0.5rem 0;
  }
`;

const isExcel = (fileName) => fileName.toLowerCase().endsWith(".xlsx");

/**
 * Builds the import payload for an uploaded file. The shape matches what the
 * file browser sends, so the same mutation and creation path handle both: the
 * staging directory becomes the source's ingress directory and the stored file
 * name its file name.
 */
const buildImportPayload = ({
  connectionId,
  upload,
  hasHeaderRow,
  valueSeparator,
  permissions,
}) => {
  const excel = isExcel(upload.fileName);
  const fileInfo = {
    directoryPathToFile: upload.uploadDirectory,
    fileNameWithWildcard: upload.fileName,
    hasHeaderRow,
    containerName: null,
    rowElement: null,
  };

  return {
    connectionId,
    dataSourcesToImport: [
      {
        name: upload.fileName,
        path: `${upload.uploadDirectory}/${upload.fileName}`,
        parentPath: upload.uploadDirectory,
        container: "",
        extension: excel ? ".xlsx" : ".csv",
        itemType: "FILE",
        ingressInformation: {
          ingressDetailsType: excel ? "EXCEL_FILE" : "CHARACTER_SEPARATED_FILE",
          // The server forces Delete for uploads; sent for shape consistency.
          archiveAction: "DELETE",
          connectionId,
          fileIngressDetails: excel ? fileInfo : null,
          characterSeparatedFileInfo: excel
            ? null
            : { ...fileInfo, valueSeparator, quoteCharacter: null },
        },
        egressInformation: [],
        tagInstances: [],
        permissions,
      },
    ],
  };
};

/**
 * `connectionId` is optional. Uploading can start from the data sources list, where
 * no connection is in view; the server resolves the upload connection itself and
 * returns which one it used, so the website does not carry a copy of that id.
 */
const UploadSource = ({
  data,
  connectionId,
  onComplete,
  importErrors,
  onSourceCreated,
}) => {
  const sourceConnectionId = connectionId ?? data?.id;

  const {
    file,
    setFile,
    upload,
    uploadError,
    progress,
    uploading,
    startUpload,
    reset,
  } = useUploadFile(sourceConnectionId);

  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [valueSeparator, setValueSeparator] = useState(",");
  const [permissions, setPermissions] = useState([]);

  const { collision, checkForCollision, clearCollision } =
    useUploadedSourceCollision();

  // Set once the user has decided what a collision means: replace the file behind the
  // source already holding that name, rather than create a second one. Until it is set
  // the collision notice stands in front of the rest of the form, because creating is
  // the one thing that cannot be done while the name is taken.
  const [replacing, setReplacing] = useState(null);

  // Choosing the file is what reveals a collision, so the check starts here rather than
  // when the upload does — the choice is worth offering before sending 150 MB.
  const chooseFile = (chosen) => {
    setFile(chosen);
    setReplacing(null);
    checkForCollision(chosen?.name);
  };

  // Clears the transfer and everything decided about it, so the form returns to asking
  // for a file. The collision belongs to the file that was chosen, not to the form.
  const startOver = useCallback(() => {
    reset();
    setReplacing(null);
    clearCollision();
    replacementRequested.current = false;
    replacementHandled.current = false;
  }, [reset, clearCollision]);

  // An uploaded source cannot inherit its connection's permissions: that connection is
  // reachable by everyone so the whole environment can upload, and inheriting it would
  // show one person's file to all of them. Somebody has to be named, so creation is
  // blocked until they are — the server refuses it too.
  const hasGrant = permissions.some((p) => p.permissionCode > 0);

  const { connectionImportFinished, setConnectionImportFinished } =
    useConnectionImportFinished();

  const [{ errors: importSourcesErrors }, importSources] = useApi();

  const [{ errors: replaceErrors, data: replaced }, doReplace] = useApi();

  // Held locally rather than derived from the mutation result: the mutation only queues
  // the work, so its result arrives at the start of the wait and never changes again.
  // Deriving from it left the overlay up for good once the work finished.
  const [creating, setCreating] = useState(false);

  // A rejected mutation means the work was never queued, so no completion notification is
  // coming and nothing else would take the overlay down.
  useEffect(() => {
    if (importSourcesErrors?.length) {
      setCreating(false);
    }
  }, [importSourcesErrors]);

  // Fired as soon as the transfer lands, because replacing was already chosen before it
  // started. The staged file is all this needed, so there is nothing further to ask.
  // Guarded because the upload result stays set: without it every later render would send
  // the mutation again.
  const replacementRequested = useRef(false);
  useEffect(() => {
    if (!replacing || !upload || replacementRequested.current) return;
    replacementRequested.current = true;
    doReplace({
      query: replaceUploadedFile,
      variables: {
        id: replacing.id,
        uploadDirectory: upload.uploadDirectory,
        fileName: upload.fileName,
      },
    });
  }, [replacing, upload, doReplace]);

  // Replacing is a plain mutation rather than queued work, so its own result is the end
  // of it -- no import-finished notification is coming. Guarded because the result stays
  // set: without it, any later render would close the dialog a second time.
  const replacementHandled = useRef(false);
  useEffect(() => {
    if (!replaced || replacementHandled.current) return;
    replacementHandled.current = true;
    startOver();
    onComplete();
  }, [replaced, startOver, onComplete]);

  useEffect(() => {
    if (connectionImportFinished) {
      const { ErrorMessage, CreatedDataSourceIds } =
        connectionImportFinished.payload;
      setConnectionImportFinished(null);
      setCreating(false);

      // Source creation runs on a background runner, so a failure arrives here rather
      // than as a mutation error. Without surfacing it the form simply resets and the
      // user is left looking at a connection with no new source and no explanation.
      if (ErrorMessage) {
        // The staged file is left in place: it is still in its staging directory, so
        // the source can be created again without uploading it a second time.
        importErrors(ErrorMessage);
        return;
      }

      reset();
      onComplete();

      // Taken to the source that was just created, where the caller wants that. Only for a
      // single id: this notification also serves browse-and-import, where a run can create
      // many sources and there is no one source to open. Guarded on the field as well, so an
      // older runner that does not report ids simply leaves the user where they were.
      if (onSourceCreated && CreatedDataSourceIds?.length === 1) {
        onSourceCreated(CreatedDataSourceIds[0]);
      }
    }
  }, [
    connectionImportFinished,
    setConnectionImportFinished,
    onComplete,
    importErrors,
    reset,
    onSourceCreated,
  ]);

  const createSource = () => {
    setCreating(true);
    importSources({
      query: importDataSources,
      variables: buildImportPayload({
        // The server tells us where it staged the file, so an upload started without a
        // connection still imports against the right one.
        connectionId: upload.connectionId,
        upload,
        hasHeaderRow,
        valueSeparator,
        permissions,
      }),
    });
  };

  return (
    <div>
      {creating && (
        <Creating>
          <SplashLoader text="Initializing Data Source" />
        </Creating>
      )}

      {uploadError ? <ErrorMessages errors={uploadError} /> : null}
      {importSourcesErrors ? (
        <ErrorMessages errors={importSourcesErrors} />
      ) : null}
      {replaceErrors ? <ErrorMessages errors={replaceErrors} /> : null}

      {!file ? (
        <DropZone
          accept={ACCEPTED_EXTENSIONS}
          acceptDescription="csv or xlsx"
          maxBytes={MAX_UPLOAD_BYTES}
          maxDescription="150 MB"
          onFileSelected={chooseFile}
        />
      ) : (
        <>
          <StagedFile>
            <span>
              {file.name} ({formatFileSize(file.size)})
            </span>
            {!uploading && (
              <button type="button" onClick={startOver} title="Remove">
                <MdDelete />
              </button>
            )}
          </StagedFile>

          {/* A name already in use stands in front of everything else, because creating
              is the one thing that cannot be done while it is taken. Loading the new data
              into the source already holding the name is almost always what was meant, so
              that is offered here rather than left as a trip to the other screen. */}
          {collision && !replacing ? (
            <Collision>
              <p>
                You already have a data source named{" "}
                <strong>{collision.name}</strong>. Uploading this file would create a
                second one with the same name.
              </p>
              {/* The consequences belong here, where the choice is actually made, rather
                  than behind a further confirmation that only repeats the question. */}
              <p>
                Replacing its file changes the data that source reports on the next time
                it runs. Its mapping, policies and run history are kept, and the data
                already loaded stays in place until you run the report again.
              </p>
              <Actions>
                <Button type="button" onClick={() => setReplacing(collision)}>
                  Replace its file
                </Button>
                <Button type="button" onClick={startOver}>
                  Choose a different file
                </Button>
              </Actions>
            </Collision>
          ) : !upload ? (
            <>
              {progress !== null ? (
                <Progress>Uploading… {progress}%</Progress>
              ) : null}
              <Actions>
                {/* One action, because the decision was made above. Asking again after
                    the transfer would be the same question with the file already sent,
                    and a cancel there could only abandon a staged file. */}
                <Button type="button" onClick={startUpload} disabled={uploading}>
                  {uploading ? (
                    <Spinner />
                  ) : replacing ? (
                    "Upload and replace"
                  ) : (
                    "Upload"
                  )}
                </Button>
                <Button type="button" onClick={startOver}>
                  Cancel
                </Button>
              </Actions>
            </>
          ) : replacing ? (
            // A failed replacement would otherwise leave this waiting for a result that
            // is never coming, with the error above it and no way back to the form. The
            // staged file is left where it is; the cleanup sweep collects it.
            replaceErrors?.length ? (
              <Actions>
                <Button type="button" onClick={startOver}>
                  Start over
                </Button>
              </Actions>
            ) : (
              <Progress>Replacing the file behind {replacing.name}…</Progress>
            )
          ) : (
            <>
              <h4>Source Details</h4>
              <FormControl>
                <label>
                  <input
                    type="checkbox"
                    checked={hasHeaderRow}
                    onChange={(e) => setHasHeaderRow(e.target.checked)}
                  />
                  &nbsp;First row contains column names
                </label>
              </FormControl>

              {!isExcel(upload.fileName) && (
                <FormControl>
                  <Label>Value Separator</Label>
                  <StyledInput
                    type="text"
                    name="valueSeparator"
                    value={valueSeparator}
                    onChange={(e) => setValueSeparator(e.target.value)}
                  />
                </FormControl>
              )}

              <FormControl>
                <Label>Who can see this source</Label>
                {/* The same control the per-source permissions screen uses, so the choice
                    made here and the one made later are the same choice. Its work group
                    list already excludes the platform-internal group every user belongs
                    to, so "everyone" cannot be picked by accident. */}
                <PermissionsSelect
                  field={{ name: "permissions" }}
                  form={{
                    values: { permissions },
                    setFieldValue: (_name, value) => setPermissions(value),
                  }}
                  // Short by default here: the grid sits mid-form inside a modal,
                  // and at its usual size it pushed Create Data Source past the
                  // bottom of the screen with no way to scroll to it. Raising rows
                  // per page still can't grow it past the cap — it scrolls instead.
                  rowsPerPage={5}
                  maxHeight="14rem"
                />
              </FormControl>

              <Actions>
                <Button
                  type="button"
                  onClick={createSource}
                  disabled={!hasGrant}
                  title={
                    hasGrant
                      ? undefined
                      : "Give at least one work group access before creating the source"
                  }
                >
                  Create Data Source
                </Button>
                <Button type="button" onClick={startOver}>
                  Cancel
                </Button>
              </Actions>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UploadSource;
